import LoadingSpinner from '@/components/LoadingSpinner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { requirePermission } from '@/lib/permissions/permissions'
import { trpc } from '@/lib/trpc/client'
import type { trpcVanilla } from '@/lib/trpc/vanilla'
import { Json } from '@/lib/supabase/supabaseTypes'
import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import {
  CheckCircle2,
  Clock3,
  Copy,
  Gift,
  Link2,
  Ticket,
  TrendingUp,
  Users,
} from 'lucide-react'
import { useMemo } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_auth/_dashboard/refer')({
  beforeLoad: () => {
    requirePermission('billing.manage')
  },
  component: ReferPage,
})

type ReferralDashboardData = Awaited<
  ReturnType<(typeof trpcVanilla)['referrals']['dashboard']['query']>
>
type ReferralCodeSummary = NonNullable<ReferralDashboardData['activeCode']>
type ReferralAttributionSummary = ReferralDashboardData['attributions'][number]
type ReferralRewardSummary = ReferralDashboardData['rewards'][number]

const cacheTtlMs = 60 * 1000
const DEFAULT_REWARD_AMOUNTS_BY_CURRENCY = {
  usd: 20,
  aud: 30,
  gbp: 15,
  eur: 15,
} as const

function ReferPage() {
  const { data, isLoading, isFetching, error } =
    trpc.referrals.dashboard.useQuery(undefined, {
      staleTime: cacheTtlMs,
    })

  const activeCode = data?.activeCode ?? null
  const attributions = data?.attributions ?? []
  const rewards = data?.rewards ?? []
  const orgBillingCurrency = data?.orgBillingCurrency ?? null
  const rewardByAttribution = useMemo(
    () => new Map(rewards.map((reward) => [reward.attribution_id, reward])),
    [rewards],
  )
  const rewardHeadline = useMemo(
    () => describeRewardHeadline(activeCode, orgBillingCurrency),
    [activeCode, orgBillingCurrency],
  )

  const shareLink = useMemo(() => {
    if (!activeCode || typeof window === 'undefined') return ''
    return new URL(
      `/register?aff=${encodeURIComponent(activeCode.code)}`,
      window.location.origin,
    ).toString()
  }, [activeCode])

  const pendingCount = attributions.filter(
    (row) => row.status === 'pending',
  ).length
  const processingCount = attributions.filter(
    (row) => row.status === 'processing',
  ).length
  const completedCount = attributions.filter(
    (row) => row.status === 'completed',
  ).length

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success(`${label} copied`)
    } catch (copyError) {
      console.error(`Unable to copy ${label.toLowerCase()}`, copyError)
      toast.error(`Unable to copy ${label.toLowerCase()}`)
    }
  }

  const queryError = error instanceof Error ? error : null

  return (
    <div className="min-h-full">
      <div className="mx-auto w-full max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[2rem] border bg-card shadow-sm">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.12),_transparent_38%),radial-gradient(circle_at_top_right,_rgba(16,185,129,0.10),_transparent_36%)]" />
          <div className="relative grid gap-8 px-6 py-8 lg:grid-cols-[1.3fr_0.9fr] lg:px-10 lg:py-10">
            <div className="space-y-6">
              <div className="space-y-3">
                <h1 className="max-w-3xl text-4xl font-black tracking-tight text-foreground sm:text-5xl">
                  Share your code with coaches and earn on every paid referral.
                </h1>
                <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
                  Trainrr tracks signups, first payments, and reward status for
                  every coach who joins through your referral link.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <SummaryCard
                  icon={Users}
                  label="Pending signups"
                  value={pendingCount}
                  isLoading={isLoading || isFetching}
                />
                <SummaryCard
                  icon={Clock3}
                  label="Awaiting reward"
                  value={processingCount}
                  isLoading={isLoading || isFetching}
                />
                <SummaryCard
                  icon={CheckCircle2}
                  label="Completed rewards"
                  value={completedCount}
                  isLoading={isLoading || isFetching}
                />
              </div>
            </div>

            <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary via-primary to-blue-700 text-primary-foreground shadow-[0_30px_80px_rgba(37,99,235,0.25)]">
              <CardContent className="flex h-full flex-col justify-between gap-6 p-6">
                <div className="space-y-3">
                  <div className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/90">
                    <Gift className="mr-2 size-4" />
                    Referral Reward
                  </div>
                  {isLoading || isFetching ? (
                    <>
                      <Skeleton className="h-10 w-64 bg-white/25" />
                      <Skeleton className="h-5 w-72 bg-white/20" />
                    </>
                  ) : (
                    <>
                      <p className="text-3xl font-black leading-tight sm:text-4xl">
                        {rewardHeadline}
                      </p>
                      <p className="text-sm leading-6 text-blue-100 sm:text-base">
                        Each coach that uses your code on signup and subscribes
                        to a plan earns you money toward your Trainrr plan.
                      </p>
                    </>
                  )}
                </div>

                <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white/90">
                    <TrendingUp className="size-4" />
                    Referred coach benefit
                  </div>
                  <p className="text-sm leading-6 text-blue-100">
                    {activeCode
                      ? describeOfferBenefit(activeCode)
                      : 'Referral offer details will appear here once your code is active.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {queryError ? (
          <Alert variant="destructive">
            <AlertTitle>Unable to load referral data</AlertTitle>
            <AlertDescription>{queryError.message}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <Card className="border-border/70 shadow-sm">
            <CardHeader>
              <CardTitle>Your referral code</CardTitle>
              <CardDescription>
                Share your link anywhere you talk to coaches and Trainrr will
                attribute the signup automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {isLoading || isFetching ? (
                <div className="space-y-3">
                  <Skeleton className="mx-auto h-20 w-full max-w-md" />
                  <Skeleton className="mx-auto h-5 w-72" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : activeCode ? (
                <>
                  <div className="rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/[0.08] via-background to-emerald-500/[0.08] px-6 py-8 text-center">
                    <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground shadow-sm">
                      <Ticket className="size-3.5" />
                      Active referral code
                    </div>
                    <div className="mx-auto flex max-w-md items-center justify-center rounded-2xl border border-primary/20 bg-background/95 px-6 py-5 shadow-sm">
                      <span className="font-mono text-3xl font-black uppercase tracking-[0.28em] text-foreground sm:text-4xl">
                        {activeCode.code}
                      </span>
                    </div>
                    <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-muted-foreground">
                      {describeOfferBenefit(activeCode)}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input readOnly value={shareLink} />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleCopy(activeCode.code, 'Code')}
                    >
                      <Copy className="mr-2 size-4" />
                      Code
                    </Button>
                    <Button
                      type="button"
                      onClick={() => handleCopy(shareLink, 'Link')}
                    >
                      <Link2 className="mr-2 size-4" />
                      Link
                    </Button>
                  </div>
                </>
              ) : (
                <Alert>
                  <AlertTitle>No referral code available yet</AlertTitle>
                  <AlertDescription>
                    Your default code has not been created yet. Once it exists,
                    it will appear here automatically.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/70 shadow-sm">
            <CardHeader>
              <CardTitle>How it works</CardTitle>
              <CardDescription>
                A quick snapshot of the referral lifecycle.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ProcessStep
                step="1"
                title="Coach signs up with your code"
                description="They land on your referral link or enter your code during registration."
              />
              <ProcessStep
                step="2"
                title="They upgrade to a paid plan"
                description="Once the first successful payment lands, the referral moves into reward processing."
              />
              <ProcessStep
                step="3"
                title="You earn the reward"
                description="Rewards become eligible after the delay window and then move to completed once processed."
              />
            </CardContent>
          </Card>
        </div>

        <Card className="border-border/70 shadow-sm">
          <CardHeader>
            <CardTitle>Referral activity</CardTitle>
            <CardDescription>
              Track who has signed up, converted, and reached reward completion.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading || isFetching ? (
              <div className="flex min-h-40 items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : attributions.length === 0 ? (
              <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                No referrals have been attributed to your code yet.
              </div>
            ) : (
              <div className="space-y-4">
                {attributions.map((attribution) => {
                  const reward = rewardByAttribution.get(attribution.id) ?? null

                  return (
                    <div
                      key={attribution.id}
                      className="rounded-2xl border bg-background p-4 shadow-sm"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge
                              variant={statusVariantByAttribution(
                                attribution.status,
                              )}
                            >
                              {statusLabelByAttribution(attribution.status)}
                            </Badge>
                            <span className="text-sm font-medium">
                              Code {attribution.final_code}
                            </span>
                            {reward ? (
                              <Badge variant="outline">
                                <Gift className="mr-2 size-3.5" />
                                {rewardLabel(reward)}
                              </Badge>
                            ) : null}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {describeAttributionTimeline(attribution, reward)}
                          </p>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <div>
                            Captured{' '}
                            {format(
                              new Date(attribution.captured_at),
                              'MMM d, yyyy',
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  isLoading,
}: {
  icon: typeof Users
  label: string
  value: number
  isLoading: boolean
}) {
  return (
    <Card className="border-border/70 bg-background/80 shadow-none backdrop-blur">
      <CardContent className="flex items-center gap-4 p-5">
        <div className="rounded-full bg-muted p-3">
          <Icon className="size-5" />
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{label}</p>
          {isLoading ? (
            <Skeleton className="h-7 w-12" />
          ) : (
            <p className="text-2xl font-semibold">{value}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function ProcessStep({
  step,
  title,
  description,
}: {
  step: string
  title: string
  description: string
}) {
  return (
    <div className="flex gap-4 rounded-2xl border bg-muted/30 p-4">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {step}
      </div>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}

function describeOfferBenefit(code: ReferralCodeSummary) {
  if (code.offer_type === 'stripe_discount') {
    const offerConfig = readObject(code.offer_config)
    const discountPercentage =
      typeof offerConfig.percent_off === 'number'
        ? offerConfig.percent_off
        : null
    const discountAmount =
      typeof offerConfig.amount_off === 'number' ? offerConfig.amount_off : null
    const currency =
      typeof offerConfig.currency === 'string' ? offerConfig.currency : null
    const durationInMonths =
      typeof offerConfig.duration_in_months === 'number'
        ? offerConfig.duration_in_months
        : null

    if (discountPercentage) {
      return durationInMonths
        ? `Referred coaches get ${discountPercentage}% off for ${durationInMonths} months when they sign up with this code.`
        : `Referred coaches get ${discountPercentage}% off when they sign up with this code.`
    }

    if (discountAmount) {
      return durationInMonths
        ? `Referred coaches get ${formatCurrency(discountAmount, currency)} off for ${durationInMonths} months when they sign up with this code.`
        : `Referred coaches get ${formatCurrency(discountAmount, currency)} off when they sign up with this code.`
    }

    return 'Referred coaches get a discount when they sign up with this code.'
  }

  const offerConfig = readObject(code.offer_config)
  const trialDays =
    typeof offerConfig.trial_days === 'number' ? offerConfig.trial_days : null

  if (trialDays) {
    return `Referred coaches get a ${trialDays}-day free trial when they sign up with this code.`
  }

  return 'Referred coaches get an extended free trial when they sign up with this code.'
}

function describeRewardHeadline(
  code: ReferralCodeSummary | null,
  orgBillingCurrency: string | null,
) {
  if (!code) {
    return 'Earn rewards for each coach you refer'
  }

  const rewardConfig = readObject(code.reward_config)
  const rewardCurrency = (orgBillingCurrency ?? 'USD').toUpperCase()
  const rewardAmount = resolveRewardAmountForCurrency(
    rewardConfig,
    rewardCurrency,
  )

  if (rewardAmount != null) {
    return `Earn ${formatCurrency(rewardAmount, rewardCurrency)} for each coach you refer`
  }

  return 'Earn rewards for each coach you refer'
}

function describeAttributionTimeline(
  attribution: ReferralAttributionSummary,
  reward: ReferralRewardSummary | null,
) {
  if (attribution.status === 'completed') {
    return attribution.completed_at
      ? `Reward completed on ${format(new Date(attribution.completed_at), 'MMM d, yyyy')}.`
      : 'Reward completed.'
  }

  if (attribution.status === 'processing') {
    const firstPayment = attribution.first_payment_at
      ? format(new Date(attribution.first_payment_at), 'MMM d, yyyy')
      : null
    const eligibleAt = reward?.eligible_at ?? attribution.eligible_at
    const eligibleDate = eligibleAt
      ? format(new Date(eligibleAt), 'MMM d, yyyy')
      : null

    if (firstPayment && eligibleDate) {
      return `First payment landed on ${firstPayment}. Reward becomes eligible on ${eligibleDate}.`
    }

    if (firstPayment) {
      return `First payment landed on ${firstPayment}. Reward is now processing.`
    }

    return 'The first payment has been received and the reward is processing.'
  }

  return 'Signed up and trial is active. Waiting for the first successful payment.'
}

function rewardLabel(reward: ReferralRewardSummary) {
  if (reward.reward_amount != null) {
    return formatCurrency(Number(reward.reward_amount), reward.reward_currency)
  }

  return reward.reward_type === 'billing_credit'
    ? 'Billing credit'
    : 'Affiliate payout'
}

function statusLabelByAttribution(
  status: ReferralAttributionSummary['status'],
) {
  switch (status) {
    case 'completed':
      return 'Completed'
    case 'processing':
      return 'Processing'
    default:
      return 'Pending'
  }
}

function statusVariantByAttribution(
  status: ReferralAttributionSummary['status'],
): 'yellow' | 'secondary' | 'green' {
  switch (status) {
    case 'completed':
      return 'green'
    case 'processing':
      return 'secondary'
    default:
      return 'yellow'
  }
}

function formatCurrency(amount: number, currency: string | null) {
  const normalizedCurrency = currency?.toUpperCase() ?? 'USD'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${normalizedCurrency} ${amount.toFixed(2)}`
  }
}

function readObject(value: unknown): Record<string, Json> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, Json>
  }
  return {}
}

function resolveRewardAmountForCurrency(
  rewardConfig: Record<string, Json>,
  currency: string,
) {
  const normalizedCurrency = currency.toLowerCase()
  const mapping = readObject(
    rewardConfig.amounts_by_currency ??
      rewardConfig.reward_amounts ??
      rewardConfig.currency_amounts ??
      null,
  )

  const mappedAmount = mapping[normalizedCurrency] ?? mapping[currency]
  if (typeof mappedAmount === 'number') {
    return mappedAmount
  }
  if (typeof mappedAmount === 'string') {
    const parsed = Number(mappedAmount)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  const fallbackAmount =
    rewardConfig.reward_amount ??
    rewardConfig.amount ??
    rewardConfig.fixed_amount
  if (typeof fallbackAmount === 'number') {
    return fallbackAmount
  }
  if (typeof fallbackAmount === 'string') {
    const parsed = Number(fallbackAmount)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  const defaultAmount =
    DEFAULT_REWARD_AMOUNTS_BY_CURRENCY[
      normalizedCurrency as keyof typeof DEFAULT_REWARD_AMOUNTS_BY_CURRENCY
    ]
  if (defaultAmount != null) {
    return defaultAmount
  }

  return null
}
